/**
 * @fileoverview Deactivate User mutation hook.
 * @description Wraps POST /users/{userId}/deactivate in a TanStack mutation. The ETag is the one
 * of the details the dialog loaded and showed, so a user who changed since is refused (412 / 3022)
 * and shown for review rather than acted on blindly. The hook owns the Idempotency-Key, so a retry
 * after an unknown outcome (lost response, timeout, 5xx) is a replay and a later command is a new
 * one. On success it refreshes the directory, team, summaries and filter options (the deactivation
 * moves the user's direct reports and changes the counts), and leaves the open dialog's details
 * alone.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useLifecycleMutation } from '@/features/users/hooks/useLifecycleMutation'
import { deactivateUser } from '@/features/users/services/users.service'

import type { DeactivateUserRequest, VersionedUserDetail } from '@/features/users/types/users.types'

export interface DeactivateUserVariables {
  userId: string
  payload: DeactivateUserRequest
  /** The ETag of the details the dialog showed. */
  etag: string
}

/**
 * @description Deactivates a user. The Idempotency-Key, the cache refresh and the completion
 * callback come from useLifecycleMutation, so the callback still runs if the dialog unmounts
 * while the request is in flight.
 * @param {{ onSuccess?: (result: VersionedUserDetail) => void }} [options] - What to do with the
 * result after a success.
 * @returns {import('@tanstack/react-query').UseMutationResult<VersionedUserDetail, ApiError,
 * DeactivateUserVariables>} The mutation object (mutate, isPending, error, ...).
 */
export function useDeactivateUser({
  onSuccess,
}: { onSuccess?: (result: VersionedUserDetail) => void } = {}) {
  return useLifecycleMutation<VersionedUserDetail, DeactivateUserVariables>({
    action: 'deactivate',
    /**
     * @description Sends the command with its idempotency key.
     * @param {DeactivateUserVariables} variables - The user, request and ETag.
     * @param {string} idempotencyKey - The key for this action on this user.
     * @returns {Promise<VersionedUserDetail>} The command's result.
     */
    send: ({ userId, payload, etag }, idempotencyKey) =>
      deactivateUser(userId, payload, etag, idempotencyKey),
    onSuccess,
  })
}
